import React, { ReactNode, useContext, useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons'
import { Upload, Popconfirm, Avatar} from 'antd'
import type { RcFile } from 'antd/es/upload/interface'
import type { UploadRequestOption } from 'rc-upload/lib/interface'

import { UserContext } from './UserContext'

const allowedFileTypes = [ 'image/jpeg', 'image/png' ]

type Error = {
  error: boolean
  message: IntlShape | string
}

type ChangeProfilePictureProps = {
  editMode: boolean
}

const ChangeProfilePicture: React.FC<ChangeProfilePictureProps> = ({editMode}) => {
  const { userDetails } = useContext(UserContext)
  const { changeProfilePicture } = useContext(UserContext)
  
  const [ loading, setLoading ] = useState(false)
  const [ imageUrl, setImageUrl ] = useState<string>()
  const [ error, setError ] = useState<Error>({ error: false, message: ''})

  const intl = useIntl()

  const alreadyHasProfilePicture = userDetails.profilePicture !== '' && userDetails.profilePicture !== null

  const getBase64 = (img: RcFile, callback: (url: string) => void) => {
    const reader = new FileReader()
    
    reader.addEventListener('load', () => callback(reader.result as string))
    reader.readAsDataURL(img)
  }
    
  const checkFileTypeAndSize = (file: RcFile) => {
    const isOfAllowedFileType = allowedFileTypes.includes(file.type)
    if (!isOfAllowedFileType) {
      setError({
        error: true,
        message: intl.formatMessage({ id: 'profilePicture.error.fileType' })
      })
    }
    
    const isSmallerThan3Mb = file.size / 1024 / 1024 < 3
    if (!isSmallerThan3Mb) {
      setError({
        error: true,
        message: intl.formatMessage({ id: 'profilePicture.error.fileSize' })
      })
    }
    
    return isOfAllowedFileType && isSmallerThan3Mb
  }

  const uploadImage = (options: UploadRequestOption) => {
    const { file } = options

    getBase64(file as RcFile, async (base64ImageUrl) => {
      await changeProfilePicture(base64ImageUrl)
      setImageUrl(base64ImageUrl)
      setLoading(false)
    })
  }

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <UploadOutlined />}
      <div style={{ marginTop: 8 }}>
        Upload
      </div>
    </div>
  )

  if (alreadyHasProfilePicture && !editMode) {
    return (
      <Avatar size={120} src={userDetails.profilePicture} />
    ) 
  }

  return (
    <>
      {error ? (
        <Popconfirm
          title={<FormattedMessage id="course.newCourse.settings.note" />}
          description={error.message as ReactNode}
          onConfirm={() => setError({ error: false, message: ''})}
          onCancel={() => setError({ error: false, message: ''})}
          okText={<FormattedMessage id="generic.gotIt" />}
          cancelText={<FormattedMessage id="generic.cancel" />}
          open={error.error}
        >
          <StyledUpload
            name="avatar"
            listType="picture-card"
            showUploadList={false}
            beforeUpload={checkFileTypeAndSize}
            customRequest={uploadImage}
          >
            {imageUrl ? <img src={imageUrl} alt="avatar" style={{ width: '100%' }} /> : uploadButton}
          </StyledUpload>
        </Popconfirm>
      ) : (
        <StyledUpload
          name="avatar"
          listType="picture-card"
          showUploadList={false}
          beforeUpload={checkFileTypeAndSize}
          customRequest={uploadImage}
        >
          {imageUrl ? <img src={imageUrl} alt="avatar" style={{ width: '100%' }} /> : uploadButton}
        </StyledUpload>
      )}
    </>
  )
}

export default ChangeProfilePicture

const StyledUpload = styled(Upload)`
  margin-top: 10px !important;
  width: 150px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
`